// @flow 

import { NavigateBefore } from "@mui/icons-material";
import { Divider } from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid2";
import axios from "axios";
import dayjs from "dayjs";
import { enqueueSnackbar } from "notistack";
import { Fragment, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import Delete from "../../../components/app/delete/delete";
import PageMenu from "../../../components/custom/app/pageMenu/pageMenu";
import BoolImage from "../../../components/custom/general/boolImage/boolImage";
import PageTitle from "../../../components/mui/app/pageTitle/pageTitle";
import QuickViewInfoWrap from "../../../components/mui/app/quickViewInfoWrap/quickViewInfoWrap";
import ContentLoader from "../../../components/mui/general/contentLoader/contentLoader";
import FormBoxTitle from "../../../components/mui/general/formBoxTitle/formBoxTitle";
import Modal from "../../../components/mui/general/modal/modal";
import PageContentWrapper from "../../../components/mui/general/pageContentWrapper/pageContentWrapper";
import { API } from "../../../helpers/constants";
import { handleAxiosErrors } from "../../../helpers/errorHandling";
import useUserData from "../../../hooks/user/useUserData";
import ProjectRegistration from "../components/projectRegistration";
import { PROJECTS } from "../locales/namespaces";

type Props = {};

const Details = (props: Props) => {
	let { projectId } = useParams()
	if (!projectId) {
		projectId = props.projectId
	}
	const { t } = useTranslation(PROJECTS)
	const { token } = useUserData();
	const [data, setData] = useState();
	const [projectWorks, setProjectWorks] = useState([])
	const [loading, setLoading] = useState(true)
	const [isDeletionVisible, setIsDeletionVisible] = useState(false)
	const {dateFormat} = useUserData()
	
	const handleDeletionModal = (visible) => {
		setIsDeletionVisible(visible)
	}

	useEffect(() => {
			document.title = t('Project Details')
			setLoading(true)
			axios({
				method: 'post',
				url: `${API}/en/fts/project/edit/` + projectId,
				headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
			}).then((response) => {
				setData(response.data.project[0])
				setProjectWorks(response.data.projectWorks?.[0])
				setLoading(false)
			}).catch((error) => {
				setLoading(false)
				enqueueSnackbar(handleAxiosErrors(error, t('An error occurred!')), {
					variant: 'warning',
					autoHideDuration: 3000,
				})
			})
		},
		[projectId, t])
	
	return (
		<Fragment>
			<Box className={'projects_details'} sx={{ marginTop: '20px' }}>
				<Box
					display={'flex'}
					flexWrap={'wrap'}
					justifyContent={'space-between'}
					gap={1}
				>
					<PageMenu>
						<Button
							component={Link}
							to={`/projects`}
							startIcon={<NavigateBefore/>}
						>
							{t('Return')}
						</Button>
						{/*</PermissionHOC>*/}
						<Button
							component={Link}
							to={`/project/edit/${projectId}`}
						>
							{t('Edit')}
						</Button>
					</PageMenu>
					<Button
						size={'small'}
						variant={'contained'}
						color={'error'}
						onClick={() => handleDeletionModal(true)}
					>
						{t('Delete')}
					</Button>
				</Box>
				<Box>
					<PageContentWrapper>
						<PageTitle
							title={t('Project Details')}
							withDivider
						/>
						<Grid
							container
							spacing={3}
						>
							
							<Grid size={{ xs: 12, sm: 12, md: 6 }}>
								<FormBoxTitle title={t('General')}/>
								<Box>
									<Grid
										container
										spacing={1}
									>
										
										<Grid size={{ xs: 12, sm: 12, md: 12 }}>
											<QuickViewInfoWrap label={t('Active')}>
												<BoolImage
													isChecked={data?.isActive === '1'}
													alt={t('Active')}
												/>
											</QuickViewInfoWrap>
										</Grid>
									</Grid>
									<QuickViewInfoWrap label={t('Description')}
													   highLightValue>{data?.Description}</QuickViewInfoWrap>
									{/*<QuickViewInfoWrap label={t('Foreman')}*/}
									{/*				   highLightValue>{data?.AssigneeName}</QuickViewInfoWrap>*/}
									<QuickViewInfoWrap label={t('Start Date')}
													   highLightValue>{data?.StartDt ? dayjs(data?.StartDt).format(dateFormat) : ''}</QuickViewInfoWrap>
									<QuickViewInfoWrap label={t('End Date')}
													   highLightValue>{data?.EndDt ? dayjs(data?.EndDt).format(dateFormat) : ''}</QuickViewInfoWrap>
									{/*<QuickViewInfoWrap label={t('Est. Date')}*/}
									{/*				   highLightValue>{data?.EstimDt ? dayjs(data?.EstimDt).format("DD/MM/YYYY") : ''}</QuickViewInfoWrap>*/}
									<QuickViewInfoWrap label={t('Estim. Cost')}
													   highLightValue>{data?.EstimCost}</QuickViewInfoWrap>
								</Box>
							</Grid>
							<Grid size={{ xs: 12, sm: 12, md: 6 }}>
								<FormBoxTitle title={t('Total Hours & Costs')}/>
								<Box>
									<Grid
										container
										spacing={1}
									>
										
										<Grid size={{ xs: 12, sm: 12, md: 6 }}>
											<QuickViewInfoWrap label={t('Work Time')}
															   highLightValue
															   sx={{
																   textAlign: 'right'
															   }}>
												{projectWorks?.WorkTm}
											</QuickViewInfoWrap>
											
											<QuickViewInfoWrap label={t('Extra Work Time')}
															   highLightValue
															   sx={{
																   textAlign: 'right'
															   }}>
												{projectWorks?.ExtraWorkTm}
											</QuickViewInfoWrap>
										</Grid>
										<Grid size={{ xs: 12, sm: 12, md: 6 }}>
											<QuickViewInfoWrap label={t('Work Cost')}
															   highLightValue
															   sx={{
																   textAlign: 'right'
															   }}>
												{projectWorks?.WorkCost}
											</QuickViewInfoWrap>
											<QuickViewInfoWrap label={t('Extra Work Cost')}
															   highLightValue
															   sx={{
																   textAlign: 'right'
															   }}>
												{projectWorks?.ExtraWorkCost}
											</QuickViewInfoWrap>
											<QuickViewInfoWrap label={t('Remaining')}
															   highLightValue
															   sx={{
																   textAlign: 'right'
															   }}>
												{projectWorks?.Remaining > 0
													?
													<Box sx={{backgroundColor:'lightgreen !important'}}>{projectWorks?.Remaining}</Box>
													:
													<Box sx={{ backgroundColor:'lightcoral !important'}}>{projectWorks?.Remaining}</Box>
												}
											</QuickViewInfoWrap>
										</Grid>
									</Grid>
								</Box>
							</Grid>
							<Grid size={{ md: 12 }}>
								<FormBoxTitle title={t('Registrations of Project')}/>
								<ProjectRegistration projectId={projectId}/>
							</Grid>
						</Grid>
					
					</PageContentWrapper>
				</Box>
				<Modal
					show={isDeletionVisible}
				>
					<Delete
						objectId={projectId}
						objectLabel={t('Project')}
						objectLabelValue={data?.Description}
						deletionUrl={`${API}/en/fts/project/remove`}
						redirectUrl={`/projects`}
						warningMessage={t('All data related to this project will be deleted!')}
						successMessage={t('Project deleted successfully!')}
						failureMessage={t('Unable to delete project!')}
						handleModal={handleDeletionModal}
						onClose={() => setIsDeletionVisible(false)}
					/>
				</Modal>
			</Box>
		</Fragment>
	
	);
};

export default Details